<template>
  <div :class="$style.shared_board_page">
    <shared-header
      :gantt-instance="{}"
    />
    <div :class="$style.shared_board_view">
      <board-view
        :sharedView="true"
        :tasksMap="tasksMap"
        :projectResources="projectResources"
        :columns="columns"
        :colWidth="colWidth"
        :showFilterStub="showFilterStub"
        :collapsed="collapsed"
      />
      <div :class="$style.filter_box">
        <portal-target name="portal-filter-sidebar" />
      </div>
    </div>
  </div>
</template>

<script>
import BoardView from './components/BoardView.vue';
import SharedHeader from '../sharedViews/sharedHeader/SharedHeader.vue';
import dateHelper from '$$helpers/dateFormats';

import {
  mapTaskForSharedBoard,
  compareTasks,
  taskToOptions
} from '../../helpers/boardHelpers';
import calendarModule from '../../components/calendar';
import customHelper from '../../helpers/custom';
import ganttFilterHelper from '../../helpers/gantt_filter';

export default {
  name: 'SharedBoardPage',
  components: {
    BoardView,
    SharedHeader
  },
  provide() {
    return {
      getTasksMap: this.getTasksMap,
      getProjectName: this.getProjectName,
      getCalendarConfig: this.getCalendarConfig,
      isFieldDisabled: () => true,
      customHelper
    }
  },
  data() {
    return {
      tasksMap: {},
      projectResources: [],
      columns: [],
      colWidth: 324,
      showFilterStub: false,
      collapsed: {},
      projectId: 0,
      projectResources: {},
      boardTasks: [],
      customSort: []
    }
  },
  created() {
    this.initData();

    this.$nextTick(() => {
      this.initBoard();
    });
  },
  computed: {
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    filterTasksCallback() {
      return ganttFilterHelper.getFilter({
        options: this.currentFilter?.properties,
        value: this.currentFilter?.properties.text || '',
      }, null);
    }
  },
  watch: {
    currentFilter(val) {
      if (!val || val.type !== 'sharedBoard') return;

      this.prepareGroupedTasks();
    },
  },
  methods: {
    initData() {
      this.projectId = this.$store.getters['board/projectId'];
      this.projectResources = this.$store.getters['resourcesModel/getResourcesByProjectId'](this.$store.getters['board/projectId']).reduce((acc, res) => {
        acc[res.id] = res;

        return acc;
      }, {});
      this.calendarConfig = calendarModule.getCalendarConfig(this.$store.getters['board/projectId']);
    },
    initBoard() {
      this.initProjectTasks();

      this.$nextTick(() => {
        this.prepareGroupedTasks();
      });
    },
    async initProjectTasks() {
      const tasksMap = this.$store.getters['tasksModel/tasksByIds'];

      const boardTasks = [];

      const { tasksWBSCodes } = { tasksWBSCodes: {} };
      const tasksCustomValues = this.$store.getters['columns/getValuesMapByProjectId'](this.projectId);

      Object.keys(tasksMap).forEach(taskId => {
        const task = tasksMap[taskId];

        if (this.shouldDisplayTaskOnBoard(task)) {
          const preparedTask = mapTaskForSharedBoard({
            task,
            visibleOnBoard: true,
            tasksMap,
            projectResources: this.projectResources,
            customFields: this.$store.getters['board/customFields'],
            customValues: tasksCustomValues,
            wbsCode: tasksWBSCodes[task.id],
            colors: this.$store.getters['teamSetting/colors'].data,
            groupingField: this.$store.getters['board/groupingFieldInfo'],
            statusField: this.$store.getters['board/getField'](false, 'status'),
            priorityField: this.$store.getters['board/getField'](false, 'priority'),
            dateHelper
          });

          tasksMap[task.id] = preparedTask;
          boardTasks.push(preparedTask.id);
        } else {
          tasksMap[task.id] = mapTaskForSharedBoard({ task });
        }
      });

      this.tasksMap = tasksMap;
      this.boardTasks = boardTasks;
    },
    prepareGroupedTasks() {
      const [
        groupingFieldInfo,
        sortingFieldInfo,
        getResourcesByProjectId
     ] = [
      this.$store.getters['board/groupingFieldInfo'],
      this.$store.getters['board/sortingFieldInfo'],
      this.$store.getters['resourcesModel/getResourcesByProjectId']
    ];
      let boardTasks = this.boardTasks.map((id) => this.tasksMap[id]);
      const boardColumns = [...groupingFieldInfo.options] || [];
      const columnsMap = new Map();
      if (boardColumns.length) {
        boardColumns.reduce((acc, column) => {
          acc.set(column.id, {
            ...column,
            tasks: [],
            totalHeight: 0,
            sharedView: true
          });

          return acc;
        }, columnsMap);
      }

      boardTasks = boardTasks.filter(this.filterTasksCallback);

      boardTasks.sort(compareTasks({
        ...sortingFieldInfo,
        resources: getResourcesByProjectId(this.projectId)
      }));

      this.showFilterStub = !boardTasks.length;

      if (columnsMap) {
        boardTasks.forEach(task => {
          const taskOpts = taskToOptions({ task, groupingFieldInfo });

          taskOpts.forEach((opt) => {
            let taskObj = { ...task };
            const customSort = [...this.customSort];
            const column = columnsMap.get(opt);

            // тут идет наполнения колонок тасками
            // идет уставнока position для каждой таски

            if (column && customSort.length > 0 && customSort.find(item => +item.groupKey === column.id)) {
              const group = customSort.find(item => +item.groupKey === column.id);
              const itemExist = group.tasks.find(item => item && item.taskId === taskObj.id);

              if (itemExist) {
                taskObj = { ...taskObj, position: itemExist.position };
              }
            }

            if (column) {
              taskObj.taskHeight = this.calculateTaskHeight(taskObj.id);
              taskObj.offsetTop = column.totalHeight + (column.tasks.length ? 8 : 0);
              column.totalHeight += taskObj.taskHeight + (column.tasks.length ? 8 : 0);
              column.tasks.push(taskObj);
            }
          });
        });
      }
      const newColumns = Array.from(columnsMap, ([key, value]) => value);

      // если есть кастомая сортировка, то сортируем таски в каждой колонке
      if (this.customSort.length > 0 && !this.sortingFieldInfo.id) {
        newColumns.forEach(column => {
          if (column.tasks.length && this.customSort.find(item => +item.groupKey === column.id)) {
            column.tasks.sort(compareTasks({
              key: 'custom',
              resources: this.getResourcesByProjectId(this.projectId),
            }));

            column.totalHeight = 0;
            column.tasks.forEach((task, i) => {
              task.offsetTop = column.totalHeight + (i ? 8 : 0);
              column.totalHeight += task.taskHeight + (i ? 8 : 0);
            });
          }
        });
      }

      this.columns = newColumns;
    },
    shouldDisplayTaskOnBoard(task) {
      return ['task'].includes(task.type);
    },
    calculateTaskHeight(taskId) {
      let taskHeight = 146; // минимальная высота карточки

      if (!['status', 'priority'].includes(this.$store.getters['board/groupingFieldInfo'].key)) {
        taskHeight += 32;
      }

      if (this.tasksMap[this.tasksMap[taskId]?.parent].parent) {
        taskHeight += 18;
      }

      return taskHeight;
    },
    getTasksMap() {
      return this.tasksMap;
    },
    getProjectName() {
      return this.$store.getters['headerView/publicUrlInfo'].projectName;
    },
    getCalendarConfig() {
      return this.calendarConfig;
    }
  }
};
</script>

<style module src="./less/sharedBoardPage.less"></style>
